import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  LayoutDashboard, 
  Brain, 
  FileText, 
  MessageSquare, 
  CheckSquare, 
  Bell, 
  Lightbulb,
  Coins,
  ArrowRight,
  Clock,
  AlertTriangle,
  TrendingUp,
  Zap
} from "lucide-react";
import { Link } from "wouter";
import { format, formatDistanceToNow } from "date-fns";
import { focusUniversalSearch } from "@/lib/universalSearch";

interface CommandCenterStats {
  totalSpaces: number;
  totalSources: number;
  totalThreads: number;
  totalTasks: number;
  pendingReminders: number;
  unprocessedCaptures: number;
  tasksByPriority: {
    high: number;
    medium: number;
    low: number;
  };
  creditBalance: string;
  recentActivity: Array<{
    id: string;
    action: string;
    entity_type: string;
    entity_title: string;
    created_at: string;
  }>;
}

interface Priorities {
  urgentTasks: Array<{
    id: string;
    title: string;
    priority: string;
    source_title: string;
  }>;
  todayReminders: Array<{
    id: string;
    title: string;
    reminder_date: string;
  }>;
  pendingCaptures: Array<{
    id: string;
    content: string;
    created_at: string;
  }>;
}

export default function CommandCenter() {
  const { data: stats, isLoading: statsLoading } = useQuery<CommandCenterStats>({
    queryKey: ["/api/command-center/stats"],
  });

  const { data: priorities, isLoading: prioritiesLoading } = useQuery<Priorities>({
    queryKey: ["/api/command-center/priorities"],
  });

  const getActionLabel = (action: string) => {
    const labels: Record<string, string> = {
      chat_created: "Started conversation",
      source_added: "Added source",
      space_created: "Created space",
      capture_created: "Quick capture",
      task_completed: "Completed task",
      document_created: "Added document",
    };
    return labels[action] || action.replace(/_/g, " ");
  };

  const getEntityIcon = (type: string) => {
    const icons: Record<string, JSX.Element> = {
      chat: <MessageSquare className="w-4 h-4" />,
      source: <FileText className="w-4 h-4" />,
      space: <Brain className="w-4 h-4" />,
      quick_capture: <Lightbulb className="w-4 h-4" />,
      task: <CheckSquare className="w-4 h-4" />,
    };
    return icons[type] || <FileText className="w-4 h-4" />;
  };

  return (
    <div className="container mx-auto p-6 space-y-6" data-testid="page-command-center">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2" data-testid="title-command-center">
            <LayoutDashboard className="w-8 h-8" />
            Command Center
          </h1>
          <p className="text-muted-foreground mt-1">
            Your unified view of knowledge and productivity
          </p>
        </div>
        <Link href="/quick-capture">
          <Button data-testid="button-new-capture">
            <Zap className="w-4 h-4 mr-2" />
            Quick Capture
          </Button>
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {statsLoading ? (
          Array.from({ length: 6 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <Skeleton className="h-8 w-16 mb-2" />
                <Skeleton className="h-4 w-20" />
              </CardContent>
            </Card>
          ))
        ) : (
          <>
            <Card className="hover-elevate cursor-pointer" data-testid="stat-spaces">
              <Link href="/spaces">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <Brain className="w-5 h-5 text-primary" />
                    <span className="text-2xl font-bold">{stats?.totalSpaces || 0}</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">Spaces</p>
                </CardContent>
              </Link>
            </Card>

            <Card className="hover-elevate cursor-pointer" data-testid="stat-sources">
              <Link href="/library?filter=references">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <FileText className="w-5 h-5 text-primary" />
                    <span className="text-2xl font-bold">{stats?.totalSources || 0}</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">Sources</p>
                </CardContent>
              </Link>
            </Card>

            <Card className="hover-elevate cursor-pointer" data-testid="stat-threads">
              <Link href="/chat">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <MessageSquare className="w-5 h-5 text-primary" />
                    <span className="text-2xl font-bold">{stats?.totalThreads || 0}</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">Threads</p>
                </CardContent>
              </Link>
            </Card>

            <Card className="hover-elevate cursor-pointer" data-testid="stat-tasks">
              <Link href="/task-generator">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <CheckSquare className="w-5 h-5 text-primary" />
                    <span className="text-2xl font-bold">{stats?.totalTasks || 0}</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">Tasks</p>
                </CardContent>
              </Link>
            </Card>

            <Card className="hover-elevate cursor-pointer" data-testid="stat-reminders">
              <Link href="/reminders">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <Bell className="w-5 h-5 text-primary" />
                    <span className="text-2xl font-bold">{stats?.pendingReminders || 0}</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">Reminders</p>
                </CardContent>
              </Link>
            </Card>

            <Card className="hover-elevate cursor-pointer" data-testid="stat-credits">
              <Link href="/settings?tab=subscription">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <Coins className="w-5 h-5 text-primary" />
                    <span className="text-2xl font-bold">{stats?.creditBalance || 0}</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">Credits</p>
                </CardContent>
              </Link>
            </Card>
          </>
        )}
      </div>

      {/* Priority Section */}
      <div className="grid md:grid-cols-3 gap-6">
        {/* Urgent Tasks */}
        <Card data-testid="card-urgent-tasks">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-destructive" />
              Urgent Tasks
            </CardTitle>
            <CardDescription>High priority items needing attention</CardDescription>
          </CardHeader>
          <CardContent>
            {prioritiesLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : priorities?.urgentTasks && priorities.urgentTasks.length > 0 ? (
              <div className="space-y-3">
                {priorities.urgentTasks.map((task) => (
                  <div 
                    key={task.id} 
                    className="flex items-center justify-between p-3 rounded-lg bg-destructive/10 border border-destructive/20"
                    data-testid={`task-urgent-${task.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{task.title}</p>
                      {task.source_title && (
                        <p className="text-xs text-muted-foreground truncate">{task.source_title}</p>
                      )}
                    </div>
                    <Badge variant="destructive" className="ml-2 shrink-0">High</Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-muted-foreground">
                <CheckSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No urgent tasks</p>
              </div>
            )}
            <Link href="/task-generator">
              <Button variant="ghost" className="w-full mt-3" data-testid="button-view-all-tasks">
                View All Tasks
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </CardContent>
        </Card>

        {/* Today's Reminders */}
        <Card data-testid="card-today-reminders">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="w-5 h-5 text-warning" />
              Today's Reminders
            </CardTitle>
            <CardDescription>Scheduled for today</CardDescription>
          </CardHeader>
          <CardContent>
            {prioritiesLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : priorities?.todayReminders && priorities.todayReminders.length > 0 ? (
              <div className="space-y-3">
                {priorities.todayReminders.map((reminder) => (
                  <div 
                    key={reminder.id} 
                    className="flex items-center justify-between p-3 rounded-lg bg-warning/10 border border-warning/20"
                    data-testid={`reminder-today-${reminder.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{reminder.title}</p>
                      <p className="text-xs text-muted-foreground">
                        {format(new Date(reminder.reminder_date), "h:mm a")}
                      </p>
                    </div>
                    <Bell className="w-4 h-4 text-warning ml-2 shrink-0" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-muted-foreground">
                <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No reminders for today</p>
              </div>
            )}
            <Link href="/reminders">
              <Button variant="ghost" className="w-full mt-3" data-testid="button-view-all-reminders">
                View All Reminders
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </CardContent>
        </Card>

        {/* Quick Captures to Process */}
        <Card data-testid="card-pending-captures">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-primary" />
              Quick Captures
            </CardTitle>
            <CardDescription>Ideas waiting to be organized</CardDescription>
          </CardHeader>
          <CardContent>
            {prioritiesLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : priorities?.pendingCaptures && priorities.pendingCaptures.length > 0 ? (
              <div className="space-y-3">
                {priorities.pendingCaptures.map((capture) => (
                  <div 
                    key={capture.id} 
                    className="p-3 rounded-lg bg-primary/10 border border-primary/20"
                    data-testid={`capture-pending-${capture.id}`}
                  >
                    <p className="text-sm line-clamp-2">{capture.content}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {formatDistanceToNow(new Date(capture.created_at), { addSuffix: true })}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-muted-foreground">
                <Lightbulb className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No pending captures</p>
              </div>
            )}
            <Link href="/quick-capture">
              <Button variant="ghost" className="w-full mt-3" data-testid="button-view-captures">
                Manage Captures
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity & Quick Actions */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Recent Activity */}
        <Card data-testid="card-recent-activity">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Recent Activity
            </CardTitle>
            <CardDescription>Your latest actions</CardDescription>
          </CardHeader>
          <CardContent>
            {statsLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))}
              </div>
            ) : stats?.recentActivity && stats.recentActivity.length > 0 ? (
              <div className="space-y-2">
                {stats.recentActivity.map((activity) => (
                  <div 
                    key={activity.id} 
                    className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors"
                    data-testid={`activity-${activity.id}`}
                  >
                    <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                      {getEntityIcon(activity.entity_type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">
                        {getActionLabel(activity.action)}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">
                        {activity.entity_title || "Untitled"}
                      </p>
                    </div>
                    <span className="text-xs text-muted-foreground shrink-0">
                      {formatDistanceToNow(new Date(activity.created_at), { addSuffix: true })}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-muted-foreground">
                <TrendingUp className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No recent activity</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card data-testid="card-quick-actions">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Zap className="w-5 h-5" />
              Quick Actions
            </CardTitle>
            <CardDescription>Jump to common tasks</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3">
              <Link href="/chat">
                <Button variant="outline" className="w-full h-auto py-4 flex-col gap-2" data-testid="action-new-chat">
                  <MessageSquare className="w-5 h-5" />
                  <span className="text-sm">New Chat</span>
                </Button>
              </Link>
              <Link href="/library?filter=references">
                <Button variant="outline" className="w-full h-auto py-4 flex-col gap-2" data-testid="action-add-source">
                  <FileText className="w-5 h-5" />
                  <span className="text-sm">Add Source</span>
                </Button>
              </Link>
              <Button
                variant="outline"
                className="w-full h-auto py-4 flex-col gap-2"
                onClick={focusUniversalSearch}
                data-testid="action-search"
              >
                <Brain className="w-5 h-5" />
                <span className="text-sm">Search Everything (⌘K)</span>
              </Button>
              <Link href="/daily-digest">
                <Button variant="outline" className="w-full h-auto py-4 flex-col gap-2" data-testid="action-digest">
                  <TrendingUp className="w-5 h-5" />
                  <span className="text-sm">Daily Digest</span>
                </Button>
              </Link>
              <Link href="/knowledge-graph">
                <Button variant="outline" className="w-full h-auto py-4 flex-col gap-2" data-testid="action-graph">
                  <LayoutDashboard className="w-5 h-5" />
                  <span className="text-sm">Knowledge Graph</span>
                </Button>
              </Link>
              <Link href="/forecasts">
                <Button variant="outline" className="w-full h-auto py-4 flex-col gap-2" data-testid="action-forecasts">
                  <TrendingUp className="w-5 h-5" />
                  <span className="text-sm">Forecasts</span>
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
